/**
 * Widget system types for admin-controlled visibility and ordering
 */

export interface WidgetConfig {
  id: string;
  widget_key: string;
  display_name: string;
  tab_name: string;
  display_order: number;
  is_visible: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export type WidgetKey =
  | 'north_indian_chart_d1'
  | 'd1_house_scores'
  | 'life_areas_dashboard'
  | 'vimshottari_timeline'
  | 'dasha_focus_overview'
  | 'basic_details'
  | 'planet_details'
  | 'yogas_analysis'
  | 'five_fold_friends'
  | 'shadbala_table'
  | 'ishta_kashta_table'
  | 'shodashavarga_table'
  | 'family_house_relationships'
  | 'family_compatibility_dashboard'
  | 'marriage_compatibility'
  | 'personal_muhurta_advisor'
  | 'life_event_timeline_analyzer';

export interface WidgetMetadata {
  key: WidgetKey;
  displayName: string;
  tabName: string;
  defaultOrder: number;
  defaultVisible: boolean;
  description: string;
}

/**
 * Default widget configurations (fallback if DB not available)
 */
export const DEFAULT_CALCULATOR_WIDGETS: WidgetMetadata[] = [
  {
    key: 'north_indian_chart_d1',
    displayName: 'North Indian Chart (D1)',
    tabName: 'calculator',
    defaultOrder: 1,
    defaultVisible: true,
    description: 'Rāśi chart showing planetary positions in North Indian diamond format',
  },
  {
    key: 'd1_house_scores',
    displayName: 'D1 House Scores',
    tabName: 'calculator',
    defaultOrder: 2,
    defaultVisible: true,
    description: 'House strength percentages with sign lords and planetary influences',
  },
  {
    key: 'life_areas_dashboard',
    displayName: 'Life Areas Dashboard',
    tabName: 'calculator',
    defaultOrder: 3,
    defaultVisible: true,
    description: 'Consolidated view of major life domains based on house combinations',
  },
  {
    key: 'vimshottari_timeline',
    displayName: 'Vimśottarī Timeline + Dasha Focus',
    tabName: 'calculator',
    defaultOrder: 4,
    defaultVisible: true,
    description: 'Mahādaśā periods with current focus and house strength overview',
  },
  {
    key: 'dasha_focus_overview',
    displayName: 'Dasha Focus Overview',
    tabName: 'calculator',
    defaultOrder: 5,
    defaultVisible: true,
    description: 'Standalone dasha focus table showing planetary periods, rulerships, occupancy, and transits',
  },
  {
    key: 'basic_details',
    displayName: 'Basic Details',
    tabName: 'calculator',
    defaultOrder: 6,
    defaultVisible: true,
    description: 'Birth information, coordinates, ascendant, moon sign, nakṣatras, planetary positions',
  },
  {
    key: 'planet_details',
    displayName: 'Planet Details',
    tabName: 'calculator',
    defaultOrder: 7,
    defaultVisible: true,
    description: 'Detailed planetary information: sidereal longitude, sign, house, nakshatra, dignity status',
  },
  {
    key: 'yogas_analysis',
    displayName: 'Yogas Analysis',
    tabName: 'calculator',
    defaultOrder: 8,
    defaultVisible: true,
    description: 'Present and absent yogas including Mangal Dosha and Kaal Sarp Yoga',
  },
  {
    key: 'five_fold_friends',
    displayName: 'Five-Fold Relationships',
    tabName: 'calculator',
    defaultOrder: 9,
    defaultVisible: true,
    description: 'Pañca-vargīya Maitri showing natural, temporal, and combined friendship',
  },
  {
    key: 'shadbala_table',
    displayName: 'Ṣaḍbala Table',
    tabName: 'calculator',
    defaultOrder: 10,
    defaultVisible: true,
    description: 'Six-fold strength analysis of planets (positional, directional, temporal, motional, etc.)',
  },
  {
    key: 'ishta_kashta_table',
    displayName: 'Iṣṭa & Kāṣṭa Table',
    tabName: 'calculator',
    defaultOrder: 11,
    defaultVisible: true,
    description: 'Benefic (iṣṭa) and malefic (kāṣṭa) points for each planet',
  },
  {
    key: 'shodashavarga_table',
    displayName: 'Ṣoḍaśavarga Table',
    tabName: 'calculator',
    defaultOrder: 12,
    defaultVisible: true,
    description: 'Sixteen divisional chart dignities showing varga strengths',
  },
  {
    key: 'marriage_compatibility',
    displayName: 'Marriage Compatibility',
    tabName: 'calculator',
    defaultOrder: 13,
    defaultVisible: true,
    description: 'Vedic marriage compatibility analysis between two charts including Moon distance, Gana, Nakshatra lord, Yoni, Lagna, 7th house, and Venus/Jupiter analysis',
  },
  {
    key: 'personal_muhurta_advisor',
    displayName: 'Personal Muhurta Advisor',
    tabName: 'calculator',
    defaultOrder: 14,
    defaultVisible: false,
    description: 'Find auspicious time windows for important activities based on your chart, transits, and Muhurta principles',
  },
  {
    key: 'life_event_timeline_analyzer',
    displayName: 'Life Event Timeline Analyzer',
    tabName: 'calculator',
    defaultOrder: 15,
    defaultVisible: false,
    description: 'Predict when events are most likely to happen naturally over next year (natal + dasha + transit probability analysis)',
  },
];
